/**
 * Graceful image reveal.
 *
 * Lazy images fade in as they decode rather than snapping in at full
 * opacity. It is a small thing that reads as considered — the difference between
 * a page that assembles itself and one that pops.
 *
 * Two rules govern the implementation:
 *
 * 1. Eager images are never faded.
 *
 *    The LCP candidate is always an eager image, and an element at opacity 0 has
 *    not painted — so fading it makes the metric wait on this deferred module
 *    rather than on the image. Only lazy images, which are below the fold by
 *    definition, get the fade.
 *
 * 2. The fade is opt-in from JavaScript, not CSS. The `image-fade` class goes on
 *    <html> from here, and only then does CSS set the starting opacity. If this
 *    module fails to load, images are simply visible — never invisible. A CSS-first
 *    approach would make a script failure hide the entire catalogue.
 *
 * Only opacity animates, so the whole effect is composited and
 * costs no main-thread work while scrolling.
 */

/*
 * The `image-fade` class is armed by an inline script in <head>, not here — a
 * deferred module runs after above-the-fold images have already decoded, and a
 * start state applied at that point is overwritten in the same frame.
 *
 * That script also arms a failsafe timer. Cancel it: this module is running, so
 * images will be revealed properly and the failsafe's !important rule would
 * otherwise fire mid-session and flatten every subsequent reveal.
 */
clearTimeout(window.__imageFadeFailsafe);

/** Mark an image as settled, whether it just loaded or was already cached. */
function reveal(img) {
  img.classList.add('is-loaded');
}

/**
 * Reveal on the next frame.
 *
 * Adding the end state in the same frame as the start state produces no
 * transition — the browser only interpolates between two painted states. Two
 * frames guarantees the start state has been through style and paint first,
 * which is what was silently skipping the animation on every cached image.
 */
function revealNextFrame(img) {
  requestAnimationFrame(() => requestAnimationFrame(() => reveal(img)));
}

function watch(img) {
  if (img.dataset.fadeBound) return;
  img.dataset.fadeBound = '1';

  /*
   * complete is true for cached images, which never fire load. Without this
   * check a returning visitor sees permanently transparent images — the exact
   * failure mode that makes this pattern risky when done carelessly.
   */
  if (img.complete && img.naturalWidth > 0) {
    revealNextFrame(img);
    return;
  }

  img.addEventListener('load', () => revealNextFrame(img), { once: true });
  // A broken image must still take up its space rather than stay invisible.
  img.addEventListener('error', () => reveal(img), { once: true });
}

/* Eager images are never hidden, so there is nothing to reveal. */
const SELECTOR = 'img[loading="lazy"]';

function scan(root) {
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;

  if (root.matches?.(SELECTOR)) watch(root);
  for (const img of root.querySelectorAll?.(SELECTOR) ?? []) watch(img);
}

scan(document);

/** Images arriving from the Section Rendering API, filters, or pagination. */
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) scan(node);
  }
}).observe(document.documentElement, { childList: true, subtree: true });
